/**
 * Widget Agenda + Carte - Affiche les événements groupés par date avec une carte
 * Utilise AgendaWidget et MapWidget comme composants enfants
 */

import { html, unsafeCSS, CSSResultGroup } from "lit";
import { customElement, state } from "lit/decorators.js";
import { ref, createRef, Ref } from "lit/directives/ref.js";
import { BaseWidget } from "../components/BaseWidget";
import "../components/FilterPanel";
import "../components/WidgetHeader";
import "../components/WidgetLayout";
import "../widgets/AgendaWidget";
import "../widgets/MapWidget";
import { ObjetTouristique } from "../types/apidae.d";
import { LayoutFraction } from "../components/WidgetLayout";
import agendaMapWidgetStyles from "../styles/agenda-map-widget.css?inline";
import { t } from "../utils/widgetsUtils";

@customElement("apidae-widget-agenda-carte")
export class AgendaMapWidget extends BaseWidget {
  static styles: CSSResultGroup = [
    BaseWidget.sharedStyles,
    unsafeCSS(agendaMapWidgetStyles),
  ];

  @state()
  private selectedItem: ObjetTouristique | null = null;

  @state()
  private viewMode: "agenda" | "split" = "split"; // "agenda" (full screen) or "split" (agenda+map)

  // Paramètres de recherche depuis la carte (conservés pour le scroll infini)
  private mapSearchCenter?: { type: "Point"; coordinates: [number, number] };
  private mapSearchRadius?: number;

  /** Ignore item-deselect pendant un selectMarker déclenché depuis une card (zoomend/setIcon ferment la popup). */
  private ignoreMapDeselect: boolean = false;

  private agendaWidgetRef: Ref<any> = createRef();
  private mapWidgetRef: Ref<any> = createRef();

  /**
   * Surcharge : la sélection et l'ouverture du détail sont gérées par
   * handleAgendaItemClick, handleMapItemClick et handleMapItemDetailClick
   */
  protected handleItemClick(_item: ObjetTouristique): void {
    // Ne rien faire - la sélection / ouverture du détail est gérée par les handlers spécifiques
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(
      "child-context-change",
      this.reactToChildWidgetUpdated as EventListener,
    );
  }

  protected applyFiltersVisibilityFromConfig(): void {
    const openByDefault =
      this.hasFilterFieldsConfigured() &&
      this.config?.contentCustomization?.filtersOpenByDefault === true;
    if (openByDefault && this.viewMode === "split") {
      this.viewMode = "agenda";
    }
    if (this.viewMode === "split") {
      this.filtersVisible = false;
    } else {
      this.filtersVisible = openByDefault;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(
      "child-context-change",
      this.reactToChildWidgetUpdated as EventListener,
    );
  }

  reactToChildWidgetUpdated = (event: Event) => {
    const customEvent = event as CustomEvent;
    const { source, loadNextPage, center, radius } = customEvent.detail;

    if (source === "agenda" && loadNextPage) {
      // L'agenda demande la page suivante (scroll infini)
      // Utiliser les paramètres de recherche de la carte s'ils sont définis
      this.loadData(
        this.currentPage + 1,
        false,
        this.mapSearchCenter,
        this.mapSearchRadius,
      );
    } else if (source === "map") {
      // La carte demande une recherche dans une zone
      // Stocker les paramètres pour les réutiliser lors du scroll infini
      this.mapSearchCenter = center;
      this.mapSearchRadius = radius;

      // Réinitialiser le scroll
      this.resetScroll();

      this.loadData(1, false, center, radius);
    }
  };

  firstUpdated() {
    // Synchroniser les données initiales avec les enfants après le premier rendu
    this.syncDataToChildren();
    this.syncConfigToChildren();
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    if (this.changedNotNull(changedProperties, "config")) {
      this.syncConfigToChildren();
    }

    // Détecter quand la carte devient visible (passage en mode split ou masquage des filtres) et l'initialiser
    const showFilters = this.isFiltersPanelVisible();
    const shouldShowMap = this.viewMode === "split" && !showFilters;

    if (
      (changedProperties.has("viewMode") && shouldShowMap) ||
      (changedProperties.has("filtersVisible") && shouldShowMap)
    ) {
      // La carte vient de devenir visible, attendre que le DOM soit mis à jour puis initialiser
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (this.mapWidgetRef.value) {
            const mapWidget = this.mapWidgetRef.value as any;
            // S'assurer que les données sont synchronisées avant d'initialiser
            this.syncDataToChildren();
            // Forcer une vérification et réinitialisation si nécessaire (carte repliée puis dépliée)
            if (mapWidget.checkAndReinitializeIfNeeded) {
              mapWidget.checkAndReinitializeIfNeeded();
            }
          }
        }, 150);
      });
    }

    if (this.changedNotNull(changedProperties, "loading")) {
      if (!this.loading) {
        this.syncDataToChildren();
      }
    }
  }

  /**
   * Synchronise les données (items, filteredItems, totalItems, hasMore, groupedByDateFromApi) avec les widgets enfants
   */
  private syncDataToChildren(): void {
    // Synchroniser avec l'agenda
    if (this.agendaWidgetRef.value) {
      const agendaWidget = this.agendaWidgetRef.value as any;
      agendaWidget.items = this.items;
      agendaWidget.totalItems = this.totalItems;
      agendaWidget.hasMore =
        this.totalItems > 0
          ? this.items.length < this.totalItems
          : this.items.length >= 10
            ? true
            : false;
      agendaWidget.currentPage = this.currentPage;
      agendaWidget.loading = this.loading;
      agendaWidget.groupedByDateFromApi = this.groupedByDateFromApi;
      agendaWidget.requestUpdate();
    }

    // Synchroniser avec la carte (dont selectedItemId pour que le pin soit sélectionné au dépli)
    if (this.mapWidgetRef.value) {
      const mapWidget = this.mapWidgetRef.value as any;
      mapWidget.items = this.items;
      mapWidget.selectedItemId = this.selectedItem?.id ?? null;
      mapWidget.loading = this.loading;
      mapWidget.requestUpdate();
    }
  }

  /**
   * Synchronise la config avec les widgets enfants
   */
  private syncConfigToChildren(): void {
    // Synchroniser uniquement la config (pour l'affichage : logo, styles, etc.)
    // Note: La config est aussi passée dans le template, mais on la synchronise aussi ici
    // pour s'assurer qu'elle est à jour même si elle change après le rendu initial
    if (this.config) {
      if (this.agendaWidgetRef.value) {
        const agendaWidget = this.agendaWidgetRef.value as any;
        agendaWidget.config = this.config;
        agendaWidget.requestUpdate();
      }

      if (this.mapWidgetRef.value) {
        const mapWidget = this.mapWidgetRef.value as any;
        mapWidget.config = this.config;
        mapWidget.requestUpdate();
      }
    }
  }

  /**
   * Réinitialise le scroll de l'agenda en haut et réinitialise l'IntersectionObserver
   */
  private resetScroll(): void {
    // Réinitialiser la page à 1
    this.currentPage = 1;

    // Réinitialiser aussi la page dans l'AgendaWidget si disponible
    if (this.agendaWidgetRef.value) {
      const agendaWidget = this.agendaWidgetRef.value as any;
      if (agendaWidget.currentPage !== undefined) {
        agendaWidget.currentPage = 1;
      }
      // Réinitialiser le scroll en haut pour éviter que l'IntersectionObserver déclenche un chargement immédiat
      requestAnimationFrame(() => {
        const scrollContainer = agendaWidget.shadowRoot?.querySelector(
          ".agenda-main",
        ) as HTMLElement;
        if (scrollContainer) {
          scrollContainer.scrollTop = 0;
        }
        // Réinitialiser l'IntersectionObserver pour qu'il ne déclenche pas immédiatement
        if (agendaWidget.setupIntersectionObserver) {
          agendaWidget.setupIntersectionObserver();
        }
      });
    }
  }

  protected async handleFilterChange(e: CustomEvent) {
    super.handleFilterChange(e);

    // Réinitialiser le scroll
    this.resetScroll();

    // Réinitialiser les paramètres de recherche de la carte (on revient à une recherche normale)
    this.mapSearchCenter = undefined;
    this.mapSearchRadius = undefined;
  }

  private handleAgendaItemClick(e: CustomEvent) {
    const item = e.detail.item as ObjetTouristique;

    // Vérifier si la carte est visible (mode split)
    const showFilters = this.isFiltersPanelVisible();
    const showMap = this.viewMode === "split" && !showFilters;

    // Toujours ouvrir la page de détail (comme hors mode split)
    this.detailItem = item;

    if (showMap && this.mapWidgetRef.value) {
      // Carte dépliée : sélectionner le pin sur la carte (animation de focus en arrière-plan)
      this.selectedItem = item;
      const mapWidget = this.mapWidgetRef.value as any;
      const coords = item.localisation?.geolocalisation?.geoJson?.coordinates;
      if (
        coords &&
        coords.length >= 2 &&
        mapWidget.map &&
        mapWidget.selectMarker
      ) {
        // selectMarker peut fermer temporairement la popup (zoomend/setIcon) :
        // ignorer le item-deselect associé pour conserver la sélection liste.
        this.ignoreMapDeselect = true;
        mapWidget.selectMarker(item.id);
        setTimeout(() => {
          this.ignoreMapDeselect = false;
        }, 1500);
      }
    }

    e.stopPropagation();
  }

  private handleMapItemClick(e: CustomEvent) {
    const item = e.detail.item as ObjetTouristique;
    // Ne pas ouvrir le détail, seulement sélectionner
    this.selectedItem = item;

    // Scroll vers l'item dans l'agenda et le sélectionner visuellement avec animation
    if (this.agendaWidgetRef.value) {
      const agendaWidget = this.agendaWidgetRef.value as any;
      // Mettre à jour la sélection dans l'AgendaWidget d'abord
      if (agendaWidget.selectedItemId !== undefined) {
        agendaWidget.selectedItemId = item.id;
        agendaWidget.requestUpdate();
      }

      // Attendre que le DOM soit mis à jour, puis scroller dans le conteneur de l'agenda
      setTimeout(() => {
        // Chercher l'élément dans le shadowRoot de l'AgendaWidget
        const itemElement = agendaWidget.shadowRoot?.querySelector(
          `[data-item-id="${item.id}"]`,
        ) as HTMLElement;
        if (itemElement) {
          // Ajouter une classe temporaire pour l'animation
          itemElement.classList.add("highlight-selection");
          setTimeout(() => {
            itemElement.classList.remove("highlight-selection");
          }, 1000);

          // Trouver le conteneur scrollable de l'agenda (.agenda-main)
          const scrollContainer = agendaWidget.shadowRoot?.querySelector(
            ".agenda-main",
          ) as HTMLElement;

          if (scrollContainer) {
            // Calculer la position de l'élément par rapport au conteneur
            const containerRect = scrollContainer.getBoundingClientRect();
            const elementRect = itemElement.getBoundingClientRect();

            // Calculer le scroll nécessaire pour centrer l'élément dans le conteneur
            const scrollTop =
              scrollContainer.scrollTop +
              (elementRect.top - containerRect.top) -
              containerRect.height / 2 +
              elementRect.height / 2;

            // Scroller dans le conteneur sans affecter la page globale
            scrollContainer.scrollTo({
              top: scrollTop,
              behavior: "smooth",
            });
          } else {
            // Fallback : utiliser scrollIntoView mais seulement si le conteneur n'est pas trouvé
            // (ne devrait pas arriver normalement)
            itemElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
              inline: "nearest",
            });
          }
        }
      }, 50);
    }

    // Empêcher l'ouverture du détail
    e.stopPropagation();
  }

  private handleMapItemDetailClick(e: CustomEvent) {
    const item = e.detail.item as ObjetTouristique;
    this.detailItem = item;
    this.selectedItem = item;

    // Mettre à jour la sélection dans l'AgendaWidget sans scroller
    // (la page de détails est en overlay, donc pas besoin de scroll)
    if (this.agendaWidgetRef.value) {
      const agendaWidget = this.agendaWidgetRef.value as any;
      if (agendaWidget.selectedItemId !== undefined) {
        agendaWidget.selectedItemId = item.id;
        agendaWidget.requestUpdate();
      }
    }

    // Empêcher la propagation
    e.stopPropagation();
  }

  private handleMapItemDeselect(_e: CustomEvent) {
    // Ignorer les fermetures de popup provoquées par selectMarker (zoomend / setIcon)
    if (this.ignoreMapDeselect) return;

    // Désélectionner dans l'agenda
    this.selectedItem = null;
    if (this.agendaWidgetRef.value) {
      const agendaWidget = this.agendaWidgetRef.value as any;
      if (agendaWidget.selectedItemId !== undefined) {
        agendaWidget.selectedItemId = null;
        agendaWidget.requestUpdate();
      }
    }
  }

  /**
   * Surcharge de notifyDataLoaded pour mettre à jour les enfants
   */
  protected notifyDataLoaded(_data: {
    items: ObjetTouristique[];
    totalItems: number;
    hasMore: boolean;
    page: number;
    isFirstPage: boolean;
    center?: { type: "Point"; coordinates: [number, number] };
    radius?: number;
  }): void {
    // Synchroniser les données avec les enfants
    this.syncDataToChildren();
  }

  protected handleToggleFilters(e: CustomEvent) {
    const newVisibility = e.detail.visible;

    // Si on affiche les filtres en mode split, repasser en mode agenda (les filtres masquent la carte)
    if (newVisibility && this.viewMode === "split") {
      this.viewMode = "agenda";
      // Réinitialiser la sélection comme lorsqu'on replie la carte (la carte est masquée)
      if (this.selectedItem) {
        this.selectedItem = null;
        if (this.agendaWidgetRef.value) {
          const agendaWidget = this.agendaWidgetRef.value as any;
          if (agendaWidget.selectedItemId !== undefined) {
            agendaWidget.selectedItemId = null;
            agendaWidget.requestUpdate();
          }
        }
      }
      // Mettre à jour le switcher dans le header
      this.requestUpdate();
    }

    this.filtersVisible = newVisibility;
  }

  protected async handleClearFilters() {
    super.handleClearFilters();

    // Réinitialiser le scroll
    this.resetScroll();

    // Réinitialiser les paramètres de recherche de la carte (on revient à une recherche normale)
    this.mapSearchCenter = undefined;
    this.mapSearchRadius = undefined;
  }

  private handleSwitcherChange(e: CustomEvent) {
    const newValue = e.detail.value;
    // Le switcher bascule entre "agenda" (plein écran) et "split" (agenda+map)
    const previousMode = this.viewMode;
    this.viewMode = newValue === "left" ? "agenda" : "split";

    // Si on replie la carte (passage en mode agenda), réinitialiser la sélection pour qu'elle ne reste pas affichée
    if (
      this.viewMode === "agenda" &&
      previousMode === "split" &&
      this.selectedItem
    ) {
      this.selectedItem = null;
      if (this.agendaWidgetRef.value) {
        const agendaWidget = this.agendaWidgetRef.value as any;
        if (agendaWidget.selectedItemId !== undefined) {
          agendaWidget.selectedItemId = null;
          agendaWidget.requestUpdate();
        }
      }
    }

    // Si on passe en mode split, masquer les filtres pour voir la carte
    if (this.viewMode === "split" && previousMode !== "split") {
      this.filtersVisible = false;
      // Attendre que le DOM soit mis à jour pour que la carte soit ajoutée au DOM
      this.requestUpdate();
      setTimeout(() => {
        if (this.mapWidgetRef.value) {
          const mapWidget = this.mapWidgetRef.value as any;
          // Forcer une vérification et réinitialisation si nécessaire
          // Cela gère le cas où la carte était repliée et qu'on la déplie avec 0 résultats
          if (mapWidget.checkAndReinitializeIfNeeded) {
            mapWidget.checkAndReinitializeIfNeeded();
          }

          // Si un item est sélectionné, sélectionner son pin sur la carte
          if (this.selectedItem) {
            const coords =
              this.selectedItem.localisation?.geolocalisation?.geoJson
                ?.coordinates;
            if (coords && coords.length >= 2) {
              // Attendre que la carte soit initialisée
              const checkMapReady = () => {
                if (mapWidget.map && mapWidget.mapInitialized) {
                  // Sélectionner le marqueur sur la carte (cela va aussi déclusteriser si nécessaire et ouvrir la popup)
                  if (mapWidget.selectMarker) {
                    mapWidget.selectMarker(this.selectedItem!.id);
                  }
                } else {
                  // Réessayer après un court délai
                  setTimeout(checkMapReady, 100);
                }
              };
              checkMapReady();
            }
          }
        }
      }, 150);
    }
  }

  protected getLayoutFraction(): LayoutFraction {
    const showFilters = this.isFiltersPanelVisible();

    if (this.viewMode === "split") {
      // En mode split : avec filtres -> 2-6 (filtres + agenda), sans filtres -> 4-4 (agenda + map)
      return showFilters ? "filters-content" : "split";
    } else {
      // En mode agenda : avec filtres -> 2-6 (filtres + agenda), sans filtres -> 8 (agenda)
      return showFilters ? "filters-content" : "full";
    }
  }

  protected renderWidget() {
    if (this.loading) {
      return html`
        <div class="loading">
          <div class="loading-spinner"></div>
          <p>${t("loading", this.language)}</p>
        </div>
      `;
    }

    if (this.error) {
      return this.renderErrorScreen();
    }

    const hasFilterFieldsConfigured = this.hasFilterFieldsConfigured();
    const showFilters = this.isFiltersPanelVisible();

    const layoutFraction = this.getLayoutFraction();
    // En mode split, on affiche toujours l'agenda (avec ou sans map selon les filtres)
    // En mode agenda, on affiche l'agenda
    const showAgenda = true; // Toujours afficher l'agenda
    // La map s'affiche seulement en mode split et sans filtres
    const showMap = this.viewMode === "split" && !showFilters;

    const visual = this.config?.visualCustomization ?? null;
    return html`
      <apidae-widget-layout
        .config=${{
          fraction: layoutFraction,
          showFilters: showFilters,
          headerSlot: "header",
          filtersSlot: showFilters ? "filters" : undefined,
          agendaSlot: showAgenda ? "agenda" : undefined,
          mapSlot: showMap ? "map" : undefined,
        }}
        .visualCustomization=${visual}
      >
        <div slot="header">
          <apidae-widget-header
            .config=${{
              widgetName: this.config?.name,
              logo: this.config?.visualCustomization?.logo,
              logoUrl: this.config?.visualCustomization?.logoUrl,
              language: this.language,
              showFiltersButton: hasFilterFieldsConfigured,
              showClearButton: hasFilterFieldsConfigured,
              hasActiveFilters: this.hasActiveFilters(),
              showSwitcher: true,
              switcherLeftLabel: t("agenda", this.language) || "Agenda",
              switcherRightLabel: t("map", this.language) || "Carte",
              switcherValue: this.viewMode === "agenda" ? "left" : "right",
              filtersVisible: this.filtersVisible,
              resultsCount: this.getResultsCountForDisplay(),
              primaryColor: this.config?.visualCustomization?.colors?.primary,
              secondaryColor:
                this.config?.visualCustomization?.colors?.secondary,
            }}
            .visualCustomization=${visual}
            @toggle-filters=${this.handleToggleFilters}
            @clear-filters=${this.handleClearFilters}
            @switcher-change=${this.handleSwitcherChange}
          ></apidae-widget-header>
        </div>

        ${showFilters
          ? html`
              <div slot="filters" class="filters-slot-wrapper">
                <apidae-filter-panel
                  .filterGroups=${this.filterGroups}
                  .language=${this.language}
                  .activeFilters=${this.activeFilters}
                  .dateRange=${this.dateRange}
                  .loadingMoreFacetId=${this.loadingMoreFacetId}
                  .visualCustomization=${visual}
                  @filter-change=${this.handleFilterChange}
                  @load-more-facet=${this.handleLoadMoreFacet}
                ></apidae-filter-panel>
              </div>
            `
          : ""}
        ${showAgenda
          ? html`
              <div slot="agenda" class="agendamap-agenda">
                <apidae-widget-agenda
                  ${ref(this.agendaWidgetRef)}
                  .isChildWidget=${true}
                  .widgetId=${this.widgetId}
                  .apiUrl=${this.apiUrl}
                  .useMock=${this.useMock}
                  .config=${this.config}
                  .selectedItemId=${this.selectedItem?.id || null}
                  .disableDetailView=${true}
                  hide-header-and-filters
                  @item-click=${this.handleAgendaItemClick}
                  @child-context-change=${this.reactToChildWidgetUpdated}
                  style="--filters-visible: ${showFilters ? "1" : "0"};"
                ></apidae-widget-agenda>
              </div>
            `
          : ""}
        ${showMap
          ? html`
              <div slot="map" class="agendamap-map">
                <apidae-widget-carte
                  ${ref(this.mapWidgetRef)}
                  .isChildWidget=${true}
                  .widgetId=${this.widgetId}
                  .apiUrl=${this.apiUrl}
                  .useMock=${this.useMock}
                  .config=${this.config}
                  .items=${this.items}
                  .selectedItemId=${this.selectedItem?.id || null}
                  .disableDetailView=${true}
                  hide-header-and-filters
                  hide-empty-state
                  @item-click=${this.handleMapItemClick}
                  @item-detail-click=${this.handleMapItemDetailClick}
                  @item-deselect=${this.handleMapItemDeselect}
                  @child-context-change=${this.reactToChildWidgetUpdated}
                ></apidae-widget-carte>
              </div>
            `
          : ""}
      </apidae-widget-layout>
    `;
  }

  render() {
    return html`
      <div class="widget-container">
        <div class="widget-content">${this.renderWidget()}</div>
        ${this.renderDetailView()} ${this.renderToast()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "apidae-widget-agenda-carte": AgendaMapWidget;
  }
}
